import { Delete, FilterAlt } from "@mui/icons-material";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Select,
  Tooltip,
} from "@mui/material";
import DrawerCM from "components/shared/Drawer";
import generateHeadCells from "helper/generate-head-cell";
import { useCallback, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  StyledGridIcons,
  StyledMenuItem,
  StyledStack,
} from "./DisplayClasses.style";

import TableCM from "components/shared/Table";
import PaginationCM from "components/shared/Pagination";
import FolderIcon from "@mui/icons-material/Folder";
import { class_types, school_levels, school_sections } from "constants/school";
import useHttp from "hooks/use-http";
import {
  getEducationYearRequest,
  getStudentClassesRequest,
} from "api/requests";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PAGINATION_LIMIT } from "constants";
import LoadingCM from "components/shared/Loading";

const headCells = generateHeadCells("user_class");

export default function UserClasses() {
  const navigate = useNavigate();

  const [educationalYears, setEducationalYears] = useState([]);

  // filter option state
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedLevels, setSelectedLevels] = useState("");
  const [selectedEducationalYear, setSelectedEducationalYear] = useState("");
  const [userClassesData, setUserClassesData] = useState({
    data: [{ content: "اطلاعاتی یافت نشد" }],
    count: 0,
  });

  // paginition state
  const [page, setPage] = useState(1);

  const { loading, request } = useHttp();

  const { register, control } = useForm();

  const getClasess = async (filter = true) => {
    const query = {
      skip: (page - 1) * PAGINATION_LIMIT,
      limit: PAGINATION_LIMIT,
      educational_year: selectedEducationalYear?.value ?? null,
      section: selectedSection?.value ?? null,
      level: selectedLevels?.value ?? null,
    };

    const { data: res } = await request({
      url: getStudentClassesRequest,
      values: filter
        ? query
        : { skip: (page - 1) * PAGINATION_LIMIT, limit: PAGINATION_LIMIT },
    });

    if (!res.count || !res.data.length) {
      return setUserClassesData({
        data: [{ content: "اطلاعاتی یافت نشد" }],
        count: 0,
      });
    } else {
      const transformedData = res.data.map((item) => ({
        id: item.id,
        className: item.title,
        classType: class_types[item.type],
        courceName: item.course.title,
        teacherName: `${item.teacher.first_name} ${item.teacher.last_name}`,
        educational_year: item.educational_year,
        section: item.section ?? "ثبت نشده",
        level: item.level ?? "ثبت نشده",
        file: (
          <IconButton
            color="primary"
            onClick={() => navigate(`/class/${item.id}/files`)}
          >
            <Tooltip title="مشاهده لیست">
              <FolderIcon />
            </Tooltip>
          </IconButton>
        ),
      }));

      setUserClassesData({
        data: transformedData,
        count: res.count,
      });
    }
  };

  const getEducationYear = useCallback(async () => {
    const res = await request({
      url: getEducationYearRequest,
    });
    if (res) {
      setEducationalYears(res);
      setSelectedEducationalYear({ value: res[res.length - 2] });
    }
  }, []);

  const getWithFilter = () => {
    setPage(1);
    getClasess();
  };

  const getWithoutFilter = () => {
    setPage(1);
    setSelectedEducationalYear(null);
    setSelectedLevels(null);
    setSelectedSection(null);
    getClasess(false);
  };

  useEffect(() => {
    getClasess();
    getEducationYear();
  }, [getEducationYear, page]);

  return (
    <DrawerCM title="کلاس های دانش آموز" $styledHeader>
      {loading ? (
        <LoadingCM />
      ) : (
        <>
          <StyledStack>
            <Grid container>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select-educational-year"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-educational-year">
                        انتخاب سال تحصیلی
                      </InputLabel>
                      <Select
                        id="educationalYear"
                        name="educationalYear"
                        label="انتخاب سال تحصیلی"
                        labelId="select-educational-year"
                        value={selectedEducationalYear?.value ?? ""}
                        {...register("educationalYear")}
                        onChange={(e) =>
                          setSelectedEducationalYear({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {educationalYears?.map((item, index) => {
                          return (
                            <StyledMenuItem key={index} value={item}>
                              {item + 1} - {item}
                            </StyledMenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select-section"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-section">
                        انتخاب مقطع تحصیلی
                      </InputLabel>
                      <Select
                        id="section"
                        name="section"
                        label="انتخاب مقطع تحصیلی"
                        labelId="select-section"
                        value={selectedSection?.value ?? ""}
                        {...register("section")}
                        onChange={(e) =>
                          setSelectedSection({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {school_sections.data.map((item, index) => {
                          return (
                            <StyledMenuItem key={index} value={item.name}>
                              {item.name}
                            </StyledMenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item sm={4} xs={12} p={0.5}>
                <Controller
                  control={control}
                  defaultValue={""}
                  name="select-level"
                  render={() => (
                    <FormControl fullWidth size="small">
                      <InputLabel id="select-level">
                        انتخاب پایه تحصیلی
                      </InputLabel>
                      <Select
                        id="level"
                        name="level"
                        label="انتخاب پایه تحصیلی"
                        labelId="select-level"
                        value={selectedLevels?.value ?? ""}
                        {...register("level")}
                        onChange={(e) =>
                          setSelectedLevels({
                            value: e.target.value,
                          })
                        }
                      >
                        <StyledMenuItem value={"*"}>{"همه"}</StyledMenuItem>
                        {school_levels.data?.map((item, index) => {
                          return (
                            <StyledMenuItem key={index} value={item.name}>
                              {item.name}
                            </StyledMenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={10} p={0.5}></Grid>
              <StyledGridIcons item sm={2} xs={12} p={1}>
                <IconButton color="primary" onClick={getWithFilter}>
                  <Tooltip title="اعمال فیلتر" placement="top">
                    <FilterAlt />
                  </Tooltip>
                </IconButton>
                <IconButton color="error" onClick={getWithoutFilter}>
                  <Tooltip title="حذف فیلتر" placement="top">
                    <Delete />
                  </Tooltip>
                </IconButton>
              </StyledGridIcons>
            </Grid>
          </StyledStack>
          <Grid container>
            <Grid item xs={12} my={2}>
              <TableCM
                rows={userClassesData.data}
                page={page}
                headCells={headCells}
                colspan={userClassesData.count === 0 ? "10" : ""}
                fontSize="12px"
                cellFontSize="12px"
              />
              {userClassesData.data.length < 1 ? (
                ""
              ) : (
                <PaginationCM
                  handleChange={(_, val) => setPage(val)}
                  page={page}
                  count={userClassesData.count}
                />
              )}
            </Grid>
          </Grid>
        </>
      )}
    </DrawerCM>
  );
}
